// FLAGS: personal-data
// The named roster: each row pairs a person's name and email with the employer they came in under
// and whether they turned up. Aggregates elsewhere in this feature are anonymous; this is not.
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@openup/shared/primitives/ui/table";
import { Badge } from "@openup/shared/primitives/ui/badge";
import { AttendanceRow, AttendanceRowStatus } from "@src/models/spaces";
import {
  formatDuration,
  formatSessionDateTime,
  formatSessionTime,
} from "@src/utils/format";

// Colour classes layered over the shared Badge primitive (its base provides
// the pill shape), mirroring the EmailInvites status palette.
const STATUS_STYLE: Record<AttendanceRowStatus, string> = {
  registered: "bg-green-700 text-white",
  attended: "bg-green-700 text-white",
  cancelled: "bg-gray-100 text-gray-700",
  "no-show": "bg-indigo-950 text-white",
};

const STATUS_LABEL: Record<AttendanceRowStatus, string> = {
  registered: "Registered",
  attended: "Attended",
  cancelled: "Cancelled",
  "no-show": "No-show",
};

interface AttendanceRosterProps {
  roster: AttendanceRow[];
}

/** Muted placeholder for values not known yet (attendance) or missing (employer). */
const Muted = () => <span className="text-gray-400">—</span>;

const AttendanceRoster = ({ roster }: AttendanceRosterProps) => {
  if (roster.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center text-gray-600">
        No one has registered for this session yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Employer</TableHead>
            <TableHead>Registered at</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Attendance</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roster.map((row) => (
            <TableRow key={row.email}>
              <TableCell>
                <div className="font-medium text-gray-900">{row.name}</div>
                <div className="text-xs text-gray-500">{row.email}</div>
              </TableCell>
              <TableCell>{row.employer ?? <Muted />}</TableCell>
              <TableCell>{formatSessionDateTime(row.registeredAt)}</TableCell>
              <TableCell>
                <Badge className={STATUS_STYLE[row.status]}>
                  {STATUS_LABEL[row.status]}
                </Badge>
              </TableCell>
              <TableCell>
                {row.joinedAt ? formatSessionTime(row.joinedAt) : <Muted />}
              </TableCell>
              <TableCell>
                {row.durationSeconds === null ? (
                  <Muted />
                ) : (
                  formatDuration(row.durationSeconds)
                )}
              </TableCell>
              <TableCell>
                {row.attendanceRatePercent === null ? (
                  <Muted />
                ) : (
                  `${row.attendanceRatePercent}%`
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default AttendanceRoster;
